import React, { useEffect, useMemo, useState } from 'react';
import {
  PageHeader, QuickFilters, DataTable, Tag, Button, Dialog, Field, TextInput, Select, Banner, IconButton, useCkToast, ckConfirm,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { formatHebrewDate } from '../../utils/hebrewDate';
import { useQuickCreate } from '../../next/quickCreate';
import { FormGrid, MultiCheck, apiError } from '../../next/ui';
import CredentialsDialog from './CredentialsDialog.jsx';

/** ניהול משתמשים — new design. Same endpoints as UsersManagement (/users, /roles, /exam-cities, reset-password, send-credentials). */
const emptyForm = { username: '', email: '', phone: '', roles: [], cities: [], status: 'active' };
const EMAIL_RE = /^[^\s<>@,;]+@[^\s<>@,;]+\.[^\s<>@,;]+$/;

function PasswordInput({ value, onChange, autoFocus }) {
  const [show, setShow] = useState(false);
  return (
    <div className="ck-input-adorn">
      <input className="ck-input" dir="ltr" type={show ? 'text' : 'password'} value={value} onChange={onChange} autoComplete="new-password" data-autofocus={autoFocus || undefined} />
      <IconButton icon={show ? 'eyeOff' : 'eye'} label={show ? 'הסתרת סיסמה' : 'הצגת סיסמה'} onClick={() => setShow((s) => !s)} />
    </div>
  );
}

export default function UsersManagementNext() {
  const toast = useCkToast();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quick, setQuick] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [dialogError, setDialogError] = useState('');
  const [saving, setSaving] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [credentials, setCredentials] = useState(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, rolesRes, citiesRes] = await Promise.all([
        api.get('/users'),
        api.get('/roles'),
        api.get('/exam-cities?active=1').catch(() => ({ data: { data: [] } })),
      ]);
      setUsers(usersRes.data.data || []);
      setRoles(rolesRes.data.data || []);
      setCityOptions([...new Set((citiesRes.data?.data || []).map((c) => c.name).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'he')));
    } catch (e) {
      setError(apiError(e, 'שגיאה בטעינת נתונים'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadData(); }, []);

  const openNew = () => { setEditUser(null); setForm({ ...emptyForm }); setDialogError(''); setDialogOpen(true); };
  useQuickCreate(() => openNew());
  const openEdit = (u) => {
    setEditUser(u);
    setForm({ username: u.username, email: u.email || '', phone: u.phone || '', roles: u.roles?.map((r) => r._id) || [], cities: Array.isArray(u.cities) ? [...u.cities] : [], status: u.status });
    setDialogError('');
    setDialogOpen(true);
  };

  const save = async () => {
    setDialogError('');
    if (!String(form.phone || '').trim()) { setDialogError('טלפון נדרש'); return; }
    if (!String(form.username || '').trim()) { setDialogError('שם משתמש נדרש'); return; }
    if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) { setDialogError('כתובת המייל אינה תקינה'); return; }
    setSaving(true);
    try {
      if (editUser) {
        await api.put(`/users/${editUser._id}`, { username: form.username, email: form.email, phone: form.phone, roles: form.roles, cities: form.cities, status: form.status });
        toast.success('המשתמש עודכן בהצלחה');
      } else {
        // בלי סיסמה — השרת יוצר סיסמה אקראית ומחזיר אותה פעם אחת
        const { data } = await api.post('/users', form);
        const { initialPassword, ...created } = data;
        setCredentials({ user: created, password: initialPassword, reason: 'create' });
      }
      setDialogOpen(false);
      loadData();
    } catch (e) {
      setDialogError(apiError(e, 'שגיאה'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!(await ckConfirm({ title: 'מחיקת משתמש', message: `למחוק את המשתמש "${u.username}"?`, confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete(`/users/${u._id}`);
      toast.success('המשתמש נמחק');
      loadData();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה במחיקה'));
    }
  };

  const resetPassword = async () => {
    setResetting(true);
    try {
      // ריק — השרת יוצר סיסמה אקראית
      const { data } = await api.post(`/users/${resetUser._id}/reset-password`, { newPassword });
      setCredentials({ user: resetUser, password: data.initialPassword || newPassword, reason: 'reset' });
      setResetUser(null);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה באיפוס סיסמה'));
    } finally {
      setResetting(false);
    }
  };

  const counts = useMemo(() => ({
    all: users.length,
    active: users.filter((u) => u.status === 'active').length,
    locked: users.filter((u) => u.status !== 'active').length,
    scoped: users.filter((u) => u.cities?.length).length,
  }), [users]);
  const rows = users.filter((u) => (quick === 'active' ? u.status === 'active' : quick === 'locked' ? u.status !== 'active' : quick === 'scoped' ? u.cities?.length : true));

  return (
    <>
      <PageHeader title="ניהול משתמשים" sub="משתמשי ממשק הניהול, התפקידים והערים של כל משתמש" primary={{ label: 'משתמש חדש', onClick: openNew }} />
      <QuickFilters
        value={quick}
        onChange={(k) => setQuick((c) => (c === k ? 'all' : k))}
        loading={loading && !users.length}
        items={[
          { key: 'all', label: 'כל המשתמשים', value: counts.all, icon: 'users', tone: 'neutral' },
          { key: 'active', label: 'פעילים', value: counts.active, icon: 'check', tone: 'success' },
          { key: 'locked', label: 'נעולים', value: counts.locked, icon: 'key', tone: 'danger', warn: counts.locked > 0 },
          { key: 'scoped', label: 'מוגבלים לערים', value: counts.scoped, icon: 'flag', tone: 'info' },
        ]}
      />
      <DataTable
        mode="client"
        rows={rows}
        rowKey="_id"
        loading={loading}
        error={error}
        onRetry={loadData}
        onRefresh={loadData}
        selectable={false}
        storageKey="bchinot-users"
        exportFilename="משתמשים"
        searchPlaceholder="חיפוש לפי שם משתמש, טלפון או מייל…"
        emptyTitle="אין משתמשים במערכת"
        onRowClick={openEdit}
        rowActions={(u) => [
          { key: 'edit', icon: 'edit', label: 'עריכה', onClick: () => openEdit(u) },
          { key: 'reset', icon: 'key', label: 'איפוס סיסמה', onClick: () => { setResetUser(u); setNewPassword(''); } },
        ]}
        rowMenu={(u) => [
          { key: 'edit', label: 'עריכה', icon: 'edit', onClick: () => openEdit(u) },
          { key: 'reset', label: 'איפוס סיסמה', icon: 'key', onClick: () => { setResetUser(u); setNewPassword(''); } },
          { divider: true },
          { key: 'del', label: 'מחיקה', icon: 'trash', danger: true, onClick: () => remove(u) },
        ]}
        columns={[
          { key: 'username', label: 'שם משתמש', render: (u) => <b>{u.username}</b> },
          { key: 'phone', label: 'טלפון', ltr: true, copy: true },
          { key: 'email', label: 'מייל', ltr: true, copy: true, render: (u) => (u.email || <span className="ck-muted">—</span>) },
          { key: 'roles', label: 'תפקידים', type: 'enum', getValue: (u) => (u.roles || []).map((r) => r.name), render: (u) => (u.roles?.length ? <span className="nx-item__tags">{u.roles.map((r) => <Tag key={r._id} tone="primary">{r.name}</Tag>)}</span> : <span className="ck-muted">ללא תפקיד</span>) },
          { key: 'cities', label: 'ערים', type: 'enum', getValue: (u) => (u.cities?.length ? u.cities : ['כל הערים']), render: (u) => (u.cities?.length ? <span className="nx-item__tags">{u.cities.map((c) => <Tag key={c}>{c}</Tag>)}</span> : <span className="ck-muted">כל הערים</span>) },
          { key: 'status', label: 'סטטוס', type: 'enum', getValue: (u) => (u.status === 'active' ? 'פעיל' : 'נעול'), render: (u) => <Tag tone={u.status === 'active' ? 'success' : 'danger'}>{u.status === 'active' ? 'פעיל' : 'נעול'}</Tag> },
          { key: 'createdAt', label: 'נוצר', type: 'date', render: (u) => (u.createdAt ? formatHebrewDate(u.createdAt) : '—') },
        ]}
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editUser ? `עריכת משתמש — ${editUser.username}` : 'משתמש חדש'}
        icon={editUser ? 'edit' : 'plus'}
        size="md"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>ביטול</Button>
            <Button variant="primary" disabled={saving} onClick={save}>{saving ? 'שומר…' : editUser ? 'עדכון' : 'צור משתמש'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          {dialogError ? <Banner tone="danger" icon="alert" onClose={() => setDialogError('')}>{dialogError}</Banner> : null}
          <FormGrid cols={2}>
            <Field label="שם משתמש"><TextInput ltr value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-autofocus /></Field>
            <Field label="טלפון *" hint="המספר שממנו המשתמש מתקשר למערכת — משמש גם לזיהוי">
              <TextInput ltr inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Field>
            <Field label="מייל" hint={editUser ? 'לשליחת פרטי כניסה' : 'סיסמה אקראית תיווצר אוטומטית — אחרי היצירה אפשר להעתיק או לשלוח במייל'}>
              <TextInput ltr type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@example.com" />
            </Field>
            <Field label="סטטוס">
              <Select value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={[{ value: 'active', label: 'פעיל' }, { value: 'locked', label: 'נעול' }]} />
            </Field>
          </FormGrid>
          <FormGrid cols={2}>
            <Field label="תפקידים">
              <MultiCheck value={form.roles} onChange={(v) => setForm({ ...form, roles: v })} options={roles.map((r) => ({ value: r._id, label: r.name, hint: r.description }))} emptyText="אין תפקידים" />
            </Field>
            <Field label="ערים (היקף הרשאה)" hint="ריק = גישה לכל הערים. עם ערים נבחרות — המשתמש רואה רק נתונים מערים אלו (למעט מנהל).">
              <MultiCheck value={form.cities} onChange={(v) => setForm({ ...form, cities: v })} options={cityOptions} emptyText="אין ערי מבחן פעילות" />
            </Field>
          </FormGrid>
        </div>
      </Dialog>

      <Dialog
        open={!!resetUser}
        onClose={() => setResetUser(null)}
        title={`איפוס סיסמה — ${resetUser?.username || ''}`}
        icon="key"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setResetUser(null)}>ביטול</Button>
            <Button variant="primary" icon="key" disabled={resetting || (!!newPassword && newPassword.length < 6)} onClick={resetPassword}>{resetting ? 'מאפס…' : newPassword ? 'אפס סיסמה' : 'צור סיסמה חדשה'}</Button>
          </>
        )}
      >
        <Field label="סיסמה חדשה (רשות)" hint="ריק = סיסמה אקראית תיווצר אוטומטית. ידנית — לפחות 6 תווים"><PasswordInput value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoFocus /></Field>
      </Dialog>

      <CredentialsDialog credentials={credentials} onClose={() => setCredentials(null)} onEmailSaved={loadData} />
    </>
  );
}
